import { useEffect, useMemo, useState, Fragment } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis, XAxis } from 'recharts';
import api from '@/lib/api';
import { Clock, TrendingUp, TrendingDown, Calendar as CalendarIcon, Fuel, Coins, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ============ Часовые пояса ============
const TZ_LIST = [
  { key: 'cet',    label: 'Центральная Европа', tz: 'Europe/Berlin',     hint: 'Берлин / Париж' },
  { key: 'moscow', label: 'Москва',              tz: 'Europe/Moscow',     hint: 'МСК' },
  { key: 'beijing',label: 'Пекин',               tz: 'Asia/Shanghai',     hint: 'CST' },
  { key: 'sp',     label: 'Сан-Паулу',           tz: 'America/Sao_Paulo', hint: 'BRT' },
  { key: 'ny',     label: 'Нью-Йорк (США)',      tz: 'America/New_York',  hint: 'ET' },
];

function formatInTZ(date, tz) {
  try {
    const time = new Intl.DateTimeFormat('ru-RU', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(date);
    const day = new Intl.DateTimeFormat('ru-RU', {
      timeZone: tz, weekday: 'short', day: '2-digit', month: 'short',
    }).format(date);
    return { time, day };
  } catch {
    return { time: '--:--:--', day: '' };
  }
}

function TimeZoneClocks() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="tz-clocks">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-[#0046AD]" />
        <h3 className="font-heading text-sm font-semibold text-slate-900">Мировое время</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {TZ_LIST.map(z => {
          const { time, day } = formatInTZ(now, z.tz);
          return (
            <div key={z.key} className="border border-slate-100 bg-slate-50/60 rounded-sm px-3 py-2" data-testid={`tz-${z.key}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{z.label}</div>
              <div className="font-mono text-xl font-bold tracking-tight text-slate-900 tabular-nums">{time}</div>
              <div className="text-[11px] text-slate-400">{day} · {z.hint}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Курсы и нефть ============
function Sparkline({ data, color = '#0046AD' }) {
  if (!data || data.length === 0) {
    return <div className="h-[60px] flex items-center justify-center text-xs text-slate-300">нет данных</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={70}>
      <LineChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
        <YAxis hide domain={['dataMin', 'dataMax']} />
        <XAxis dataKey="date" hide />
        <Tooltip
          contentStyle={{ fontSize: 11, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 2 }}
          labelFormatter={(d) => d}
          formatter={(v) => [Number(v).toFixed(4), 'значение']}
        />
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function RateCard({ title, sub, value, prevValue, series, color, unit = '', precision = 4, testid }) {
  const diff = value != null && prevValue != null ? value - prevValue : null;
  const pct = diff != null && prevValue ? (diff / prevValue) * 100 : null;
  const up = diff != null && diff >= 0;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
          <div className="text-xs text-slate-400">{sub}</div>
        </div>
        {diff != null && (
          <Badge variant="outline" className={`text-[11px] rounded-sm ${up ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
            {up ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {up ? '+' : ''}{pct.toFixed(2)}%
          </Badge>
        )}
      </div>
      <div className="font-mono text-2xl font-bold tracking-tight text-slate-900 tabular-nums mb-1">
        {value != null ? `${unit}${Number(value).toFixed(precision)}` : '—'}
      </div>
      <Sparkline data={series} color={color} />
    </div>
  );
}

function MarketWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get('/dashboard/market', { params: { days: 30 } });
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError('Не удалось загрузить рыночные данные');
      }
    };
    load();
    const id = setInterval(load, 15 * 60 * 1000); // 15 min refresh
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 text-sm text-rose-600">{error}</div>
    );
  }
  if (!data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[0,1,2,3].map(i => (
          <div key={i} className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 h-[150px] animate-pulse" />
        ))}
      </div>
    );
  }

  const prev = (series) => (series && series.length >= 2 ? series[series.length - 2].value : null);
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Coins className="w-4 h-4 text-[#0046AD]" />
        <h3 className="font-heading text-sm font-semibold text-slate-900">Курсы и сырьё</h3>
        <span className="text-[11px] text-slate-400">30 дней · источники: ECB / ЦБ РФ / Yahoo</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <RateCard testid="rate-eur-usd" title="EUR / USD" sub="Евро → Доллар" value={data.latest.eur_usd}
          prevValue={prev(data.history.eur_usd)} series={data.history.eur_usd} color="#0046AD" />
        <RateCard testid="rate-brl-usd" title="BRL / USD" sub="Реал → Доллар" value={data.latest.brl_usd}
          prevValue={prev(data.history.brl_usd)} series={data.history.brl_usd} color="#15803d" />
        <RateCard testid="rate-usd-rub" title="USD / RUB" sub="Доллар → Рубль (ЦБ РФ)" value={data.latest.usd_rub}
          prevValue={prev(data.history.usd_rub)} series={data.history.usd_rub} color="#b91c1c" precision={2} />
        <RateCard testid="rate-brent" title="Нефть Brent" sub="USD за баррель" value={data.latest.brent_usd}
          prevValue={prev(data.history.brent_usd)} series={data.history.brent_usd} color="#ea580c" precision={2} unit="$" />
      </div>
    </div>
  );
}

// ============ Активные тендеры ============
const ACTIVE_PHASES = ['published','waiting','collecting','waiting_bids','bidding'];

const PHASE_LABEL = {
  draft: 'Черновик', published: 'Опубликован', waiting: 'Ожидание',
  collecting: 'Сбор заявок', waiting_bids: 'Ожидание торгов',
  bidding: 'Торги', completed: 'Завершён', cancelled: 'Отменён',
};

const PHASE_CLS = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  published: 'bg-blue-50 text-blue-700 border-blue-200',
  waiting: 'bg-slate-100 text-slate-600 border-slate-200',
  collecting: 'bg-amber-50 text-amber-700 border-amber-200',
  waiting_bids: 'bg-slate-100 text-slate-600 border-slate-200',
  bidding: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completed: 'bg-slate-100 text-slate-700 border-slate-300',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

function useAllTenders() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/tenders');
        if (alive) setTenders(data || []);
      } catch {}
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  return { tenders, loading };
}

function ActiveTendersList({ tenders, loading, onOpenTender }) {
  const active = (tenders || []).filter(t => ACTIVE_PHASES.includes(t.phase) && t.status !== 'cancelled');
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="active-tenders">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-[#0046AD]" />
        <h3 className="font-heading text-sm font-semibold text-slate-900">Активные тендеры</h3>
        <Badge variant="outline" className="text-[10px] rounded-sm">{active.length}</Badge>
      </div>
      {loading ? (
        <div className="text-sm text-slate-400 py-6 text-center">Загрузка...</div>
      ) : active.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">Нет активных тендеров</div>
      ) : (
        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
          {active.map(t => (
            <button key={t.id} onClick={() => onOpenTender && onOpenTender(t)}
              data-testid={`active-tender-${t.id}`}
              className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-slate-100 bg-slate-50/50 hover:bg-slate-100 hover:border-slate-200 transition">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{t.lot_name}</div>
                <div className="text-[11px] text-slate-500 truncate">{t.product_name} · {t.volume} {t.volume_unit || 'т'} · {t.currency}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`text-[10px] rounded-sm ${PHASE_CLS[t.phase] || PHASE_CLS.published}`}>
                  {PHASE_LABEL[t.phase] || t.phase}
                </Badge>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Производственный календарь ============
// Федеральные нерабочие праздничные дни РФ. Для 2026 — приближённые (с переносами проверяется только факт «красный день»).
// В реальном продакшене список должен обновляться каждый год из официального ПП РФ.
const RU_HOLIDAYS = new Set([
  // 2026 (предварительно)
  '2026-01-01','2026-01-02','2026-01-05','2026-01-06','2026-01-07','2026-01-08',
  '2026-02-23','2026-03-09','2026-05-01','2026-05-11','2026-06-12','2026-11-04',
  // 2027
  '2027-01-01','2027-01-04','2027-01-05','2027-01-06','2027-01-07','2027-01-08',
  '2027-02-23','2027-03-08','2027-05-03','2027-05-10','2027-06-14','2027-11-04',
]);

// ISO week number
function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

function ymd(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dd}`;
}

function MonthCalendar({ year, month, tendersByDate, onOpenTender }) {
  const [popover, setPopover] = useState(null); // { key, x, y }
  const monthName = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' })
    .format(new Date(year, month, 1));

  // Build 6-row grid starting from Monday
  const weeks = useMemo(() => {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // Mon=0
    const start = new Date(year, month, 1 - offset);
    const rows = [];
    for (let w = 0; w < 6; w++) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + w * 7 + i);
        row.push(d);
      }
      rows.push(row);
    }
    return rows;
  }, [year, month]);

  const today = new Date();
  const todayKey = ymd(today);

  // Close popover on outside click
  useEffect(() => {
    if (!popover) return;
    const close = (e) => {
      if (!e.target.closest || !e.target.closest('[data-day-cell]')) setPopover(null);
    };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [popover]);

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 shadow-sm relative" data-testid={`cal-${year}-${month + 1}`}>
      <div className="text-sm font-semibold text-slate-900 capitalize mb-2">{monthName}</div>
      <div className="grid grid-cols-8 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
        <div className="text-center">нд</div>
        {['пн','вт','ср','чт','пт','сб','вс'].map(w => (
          <div key={w} className="text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-y-0.5 text-xs">
        {weeks.map((row, ri) => {
          const anchor = row.find(d => d.getMonth() === month) || row[0];
          const wk = isoWeek(anchor);
          return (
            <Fragment key={`wk-${ri}`}>
              <div className="text-center text-slate-400 font-semibold font-mono">{wk}</div>
              {row.map((d) => {
                const inMonth = d.getMonth() === month;
                const key = ymd(d);
                const isHoliday = RU_HOLIDAYS.has(key);
                const dow = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
                const isWeekend = dow >= 5;
                const isRed = isHoliday || isWeekend;
                const isToday = key === todayKey;
                const dayTenders = (tendersByDate && tendersByDate[key]) || [];
                const hasTenders = dayTenders.length > 0;
                // Цвет кружка: синий если в этот день есть хотя бы один тендер направления «Логистика»,
                // иначе зелёный (Закупка и прочие / без направления).
                const hasLogistics = dayTenders.some(t => t.direction === 'logistics');
                const ringCls = hasLogistics ? 'ring-[#0046AD]' : 'ring-emerald-500';
                const bgCls = isToday ? '' : (hasLogistics ? 'bg-blue-50' : 'bg-emerald-50');
                return (
                  <div key={key} data-day-cell
                    className={[
                      'relative text-center py-0.5 rounded-sm select-none',
                      !inMonth ? 'text-slate-300' : isRed ? 'text-rose-600 font-semibold' : 'text-slate-700',
                      isToday ? 'bg-[#0046AD] text-white font-bold' : '',
                      hasTenders ? 'cursor-pointer' : '',
                    ].join(' ')}
                    onClick={hasTenders ? (e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const parent = e.currentTarget.closest('[data-testid^="cal-"]').getBoundingClientRect();
                      setPopover({
                        key,
                        x: rect.left - parent.left + rect.width / 2,
                        y: rect.bottom - parent.top + 4,
                      });
                    } : undefined}
                    title={isHoliday ? 'Праздничный день' : (isWeekend ? 'Выходной' : (hasTenders ? `${dayTenders.length} торгов` : ''))}
                  >
                    {hasTenders && (
                      <span className={`absolute inset-0 m-auto w-6 h-6 rounded-full ring-2 ${ringCls} ${bgCls}`}
                        style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                        aria-hidden
                      />
                    )}
                    <span className="relative z-10">{d.getDate()}</span>
                  </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>
      {popover && tendersByDate && tendersByDate[popover.key] && (
        <div
          className="absolute z-20 bg-white border border-slate-200 rounded-sm shadow-lg p-2 min-w-[220px] max-w-[280px]"
          style={{ left: popover.x, top: popover.y, transform: 'translateX(-50%)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            Торги {popover.key}
          </div>
          <div className="space-y-1">
            {tendersByDate[popover.key].map(t => (
              <button
                key={t.id}
                onClick={() => { setPopover(null); onOpenTender && onOpenTender(t); }}
                className="w-full text-left px-2 py-1 rounded-sm hover:bg-slate-100 text-xs"
              >
                <div className="font-semibold text-slate-900 truncate">{t.lot_name}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {PHASE_LABEL[t.phase] || t.phase} · {t.product_name}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductionCalendar({ tenders, onOpenTender }) {
  const months = useMemo(() => {
    const now = new Date();
    const out = [];
    for (let i = 0; i < 4; i++) {
      const m = new Date(now.getFullYear(), now.getMonth() + i, 1);
      out.push({ year: m.getFullYear(), month: m.getMonth() });
    }
    return out;
  }, []);

  // Map "YYYY-MM-DD" → [tender, ...] covering the full bid period (bid_start..bid_end inclusive)
  const tendersByDate = useMemo(() => {
    const map = {};
    const parseIso = (s) => {
      if (!s) return null;
      const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
    };
    (tenders || []).forEach(t => {
      if (t.status === 'cancelled') return;
      const from = parseIso(t.bid_start);
      const to = parseIso(t.bid_end) || from;
      if (!from) return;
      const cur = new Date(from);
      while (cur <= to) {
        const key = ymd(cur);
        if (!map[key]) map[key] = [];
        map[key].push(t);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [tenders]);

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="production-calendar">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CalendarIcon className="w-4 h-4 text-[#0046AD]" />
        <h3 className="font-heading text-sm font-semibold text-slate-900">Производственный календарь (РФ)</h3>
        <span className="text-[11px] text-slate-400">текущий + 3 месяца · выходные красным</span>
        <span className="inline-flex items-center gap-2 text-[11px] text-slate-500 ml-auto flex-wrap">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full ring-2 ring-emerald-500 bg-emerald-50" /> закупка
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full ring-2 ring-[#0046AD] bg-blue-50" /> логистика
          </span>
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {months.map(m => (
          <MonthCalendar key={`${m.year}-${m.month}`} year={m.year} month={m.month}
            tendersByDate={tendersByDate} onOpenTender={onOpenTender} />
        ))}
      </div>
    </div>
  );
}

// ============ Главный компонент ============
export default function OverviewPanel({ onOpenTender }) {
  const { tenders, loading } = useAllTenders();
  return (
    <div className="space-y-4 mb-6" data-testid="overview-panel">
      <TimeZoneClocks />
      <MarketWidget />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ActiveTendersList tenders={tenders} loading={loading} onOpenTender={onOpenTender} />
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 flex flex-col justify-center" data-testid="oil-hint">
          <div className="flex items-center gap-2 mb-2">
            <Fuel className="w-4 h-4 text-[#ea580c]" />
            <h3 className="font-heading text-sm font-semibold text-slate-900">Подсказка</h3>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Курсы и нефть обновляются автоматически каждые 15 минут. Клик по тендеру открывает его карточку.
            В календаре дни с запланированными торгами подсвечены зелёным кружком — клик по такой дате
            покажет список тендеров на этот день.
          </p>
        </div>
      </div>
      <ProductionCalendar tenders={tenders} onOpenTender={onOpenTender} />
    </div>
  );
}
